import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AudioLines,
  BookAudio,
  ListMusic,
  Music2,
  Pause,
  Play,
  Plus,
  Repeat,
  Repeat1,
  Search,
  Settings2,
  SkipBack,
  SkipForward,
  Star,
  Volume2,
  VolumeX,
  EyeOff,
  PanelBottom,
  Square,
  X,
  ArrowBigRightDash,
  Shuffle,
} from 'lucide-react';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import { useAuth } from '../../context/AuthContext';
import { useSoundboard } from '../../hooks/useSoundboard';
import { cn } from '../../lib/utils';
import { DEFAULT_ARTWORK, formatDuration, formatTagLabel } from '../../lib/audioPlayerUtils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import SoundboardPanel from '../soundboard/SoundboardPanel';
import SoundboardLibrary from '../soundboard/SoundboardLibrary';

const DEFAULT_LIBRARY_TAB = 'tags';

// ─────────────────────────────────────────────────────────────────────────────
// ArtworkThumb
// ─────────────────────────────────────────────────────────────────────────────
const ArtworkThumb = ({ src, alt, size = 56, className }) => {
  const [hasError, setHasError] = useState(false);
  const resolvedSrc = !hasError && src ? src : DEFAULT_ARTWORK;

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      width={size}
      height={size}
      loading="lazy"
      onError={() => setHasError(true)}
      className={cn('shrink-0 rounded-md object-cover border border-border/60 bg-muted', className)}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// QueueList
// ─────────────────────────────────────────────────────────────────────────────
const QueueList = ({ tracksById, queue, currentTrackId, onPlay, onRemove }) => {
  if (!queue.length) {
    return <p className="text-sm text-muted-foreground">Queue vide.</p>;
  }

  return (
    <div className="space-y-2">
      {queue.map((trackId) => {
        const track = tracksById.get(trackId);
        if (!track) {
          return null;
        }

        const isCurrent = trackId === currentTrackId;
        return (
          <div
            key={`queue-${trackId}`}
            className={cn(
              'flex items-center gap-2 rounded-md border border-border/60 p-2',
              isCurrent && 'border-primary/60 bg-primary/5',
            )}
          >
            <ArtworkThumb src={track.imageUrl} alt={track.title} size={40} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{track.title || track.filename}</p>
              <p className="truncate text-xs text-muted-foreground">{track.filename}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={() => onPlay(trackId)} aria-label="Lire depuis la queue">
              <Play className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onRemove(trackId)} aria-label="Retirer de la queue">
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LibraryTrackRow
// ─────────────────────────────────────────────────────────────────────────────
const LibraryTrackRow = ({
  track,
  isFavorite,
  onPlay,
  onAddNext,
  onAddEnd,
  onToggleFavorite,
}) => {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border/60 p-2">
      <ArtworkThumb src={track.imageUrl} alt={track.title} size={48} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{track.title || track.filename}</p>
        <p className="truncate text-xs text-muted-foreground">{track.filename}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          {track.tags.length ? (
            track.tags.slice(0, 4).map((tag) => (
              <Badge key={`${track.id}-${tag}`} variant="secondary" className="text-[10px] px-1.5 py-0">
                {formatTagLabel(tag)}
              </Badge>
            ))
          ) : (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              Non classe
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="icon" onClick={() => onPlay(track.id)} aria-label="Lire">
          <Play className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onAddNext(track.id)} aria-label="Ajouter ensuite">
          <ArrowBigRightDash className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" onClick={() => onAddEnd(track.id)} aria-label="Ajouter a la fin">
          <Plus className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onToggleFavorite(track.id)}
          aria-label="Favori"
          className={cn(isFavorite && 'text-amber-500')}
        >
          <Star className={cn('h-4 w-4', isFavorite && 'fill-amber-500')} />
        </Button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PlayerDock — lecteur compact intégré dans le panel Bibliothèque
//
// Affiché en header du panel quand la bibliothèque est ouverte.
// Remplace le lecteur flottant (qui est masqué pendant l'ouverture).
// ─────────────────────────────────────────────────────────────────────────────
const PlayerDock = () => {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    muted,
    repeatMode,
    favoritesSet,
    togglePlayPause,
    playNext,
    playPrevious,
    seekTo,
    stopImmediate,
    setVolume,
    toggleMute,
    cycleRepeatMode,
    toggleFavorite,
  } = useAudioPlayer();

  const RepeatIcon = repeatMode === 'one' ? Repeat1 : Repeat;
  const trackTitle = currentTrack?.title || currentTrack?.filename || 'Aucune piste selectionnee';
  const isFavorite = currentTrack ? favoritesSet.has(currentTrack.id) : false;

  return (
    <div className="flex-shrink-0 border-b border-border/60 bg-muted/20 px-4 py-3 space-y-2">
      {/* Ligne 1 : artwork + titre + transport */}
      <div className="flex items-center gap-3">
        <ArtworkThumb
          src={currentTrack?.imageUrl}
          alt={trackTitle}
          size={44}
          className="flex-shrink-0"
        />

        <div className="min-w-0 flex-1 space-y-1.5">
          {/* Titre + bouton favori */}
          <div className="flex items-center gap-1 min-w-0">
            <p className="truncate text-sm font-semibold leading-tight flex-1">{trackTitle}</p>
            {currentTrack && (
              <button
                type="button"
                className={cn(
                  'shrink-0 flex items-center justify-center h-6 w-6 rounded transition-colors hover:bg-accent',
                  isFavorite ? 'text-amber-500' : 'text-muted-foreground',
                )}
                onClick={() => toggleFavorite(currentTrack.id)}
                aria-label="Favori"
              >
                <Star className={cn('h-3.5 w-3.5', isFavorite && 'fill-amber-500')} />
              </button>
            )}
          </div>

          {/* Boutons transport */}
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              className="flex items-center justify-center h-7 w-7 rounded transition-colors hover:bg-accent text-foreground"
              onClick={playPrevious}
              aria-label="Précédent"
            >
              <SkipBack className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="flex items-center justify-center h-8 w-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              onClick={togglePlayPause}
              aria-label={isPlaying ? 'Pause' : 'Lecture'}
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
            <button
              type="button"
              className="flex items-center justify-center h-7 w-7 rounded transition-colors hover:bg-accent text-foreground"
              onClick={playNext}
              aria-label="Suivant"
            >
              <SkipForward className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={cn(
                'flex items-center justify-center h-7 w-7 rounded transition-colors hover:bg-accent ml-1',
                repeatMode !== 'off' ? 'text-primary' : 'text-muted-foreground',
              )}
              onClick={cycleRepeatMode}
              aria-label="Mode de répétition"
            >
              <RepeatIcon className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="ml-auto flex items-center justify-center h-7 w-7 rounded transition-colors hover:bg-destructive/20 text-destructive"
              onClick={stopImmediate}
              aria-label="Arrêt immédiat"
            >
              <Square className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Ligne 2 : barre de progression */}
      <div className="flex items-center gap-2">
        <span className="w-9 text-right text-[11px] text-muted-foreground tabular-nums select-none">
          {formatDuration(progress)}
        </span>
        <Slider
          value={[duration > 0 ? (progress / duration) * 100 : 0]}
          onValueCommit={([ratio]) => seekTo((ratio / 100) * duration)}
          max={100}
          step={0.1}
          disabled={!currentTrack || duration <= 0}
          className="flex-1"
          aria-label="Progression"
        />
        <span className="w-9 text-[11px] text-muted-foreground tabular-nums select-none">
          {formatDuration(duration)}
        </span>
      </div>

      {/* Ligne 3 : volume */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="flex items-center justify-center h-6 w-6 rounded transition-colors hover:bg-accent text-muted-foreground"
          onClick={toggleMute}
          aria-label={muted ? 'Activer le son' : 'Couper le son'}
        >
          {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
        </button>
        <Slider
          value={[muted ? 0 : volume * 100]}
          onValueChange={([value]) => setVolume(value / 100)}
          max={100}
          step={1}
          className="flex-1"
          aria-label="Volume"
        />
        <span className="w-8 text-right text-[11px] text-muted-foreground tabular-nums select-none">
          {Math.round((muted ? 0 : volume) * 100)}%
        </span>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AudioLibrary — panel bibliothèque avec dock lecteur intégré
//
// Correctifs apportés :
//   1. modal={false} sur <Sheet> → désactive le focus-trap Radix UI (cause racine)
//   2. hideOverlay sur <SheetContent> → supprime l'overlay Radix (pointer-events)
//   3. Backdrop custom z-[48] avec onClick → ferme le panel au clic extérieur
//      sans bloquer les éléments à z-index supérieur
//   4. <PlayerDock /> inséré en header du panel → lecteur toujours accessible
// ─────────────────────────────────────────────────────────────────────────────
const AudioLibrary = () => {
  const {
    tracks,
    queue,
    currentTrackId,
    favoritesSet,
    recents,
    tagCounts,
    uncategorizedTracks,
    collections,
    libraryOpen,
    libraryTab,
    isMobile,
    setLibraryOpen,
    setLibraryTab,
    playTrackById,
    playFromQueue,
    addToQueueNext,
    addToQueueEnd,
    removeFromQueue,
    toggleFavorite,
    playCollectionRandom,
  } = useAudioPlayer();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagMode, setTagMode] = useState('or');
  const [sortMode, setSortMode] = useState('az');

  const tracksById = useMemo(() => new Map(tracks.map((track) => [track.id, track])), [tracks]);

  const favoritesTracks = useMemo(
    () => tracks.filter((track) => favoritesSet.has(track.id)),
    [tracks, favoritesSet],
  );

  const recentTracks = useMemo(() => {
    return recents.map((trackId) => tracksById.get(trackId)).filter(Boolean);
  }, [recents, tracksById]);

  const queueTracks = useMemo(
    () => queue.map((trackId) => tracksById.get(trackId)).filter(Boolean),
    [queue, tracksById],
  );

  const toggleTag = (tag) => {
    setSelectedTags((previous) => {
      if (previous.includes(tag)) {
        return previous.filter((value) => value !== tag);
      }
      return [...previous, tag];
    });
  };

  const baseTracks = useMemo(() => {
    switch (libraryTab) {
      case 'favorites':
        return favoritesTracks;
      case 'recents':
        return recentTracks;
      case 'queue':
        return queueTracks;
      case 'all':
      case 'tags':
      default:
        return tracks;
    }
  }, [favoritesTracks, libraryTab, queueTracks, recentTracks, tracks]);

  const filteredTracks = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    const tagFiltered = baseTracks.filter((track) => {
      if (!selectedTags.length) {
        return true;
      }

      if (tagMode === 'and') {
        return selectedTags.every((tag) => track.tags.includes(tag));
      }

      return selectedTags.some((tag) => track.tags.includes(tag));
    });

    const queryFiltered = tagFiltered.filter((track) => {
      if (!query) {
        return true;
      }

      const title = (track.title || '').toLowerCase();
      const filename = (track.filename || '').toLowerCase();
      return title.includes(query) || filename.includes(query);
    });

    const sorted = [...queryFiltered];

    if (sortMode === 'recent') {
      const recentsOrder = new Map(recents.map((id, index) => [id, index]));
      sorted.sort((a, b) => {
        const ai = recentsOrder.has(a.id) ? recentsOrder.get(a.id) : Number.POSITIVE_INFINITY;
        const bi = recentsOrder.has(b.id) ? recentsOrder.get(b.id) : Number.POSITIVE_INFINITY;
        if (ai === bi) {
          return (a.title || a.filename).localeCompare(b.title || b.filename);
        }
        return ai - bi;
      });
      return sorted;
    }

    if (sortMode === 'duration') {
      sorted.sort((a, b) => {
        const da = Number.isFinite(a.duration) ? a.duration : Number.POSITIVE_INFINITY;
        const db = Number.isFinite(b.duration) ? b.duration : Number.POSITIVE_INFINITY;
        if (da === db) {
          return (a.title || a.filename).localeCompare(b.title || b.filename);
        }
        return da - db;
      });
      return sorted;
    }

    sorted.sort((a, b) => (a.title || a.filename).localeCompare(b.title || b.filename));
    return sorted;
  }, [baseTracks, recents, searchTerm, selectedTags, sortMode, tagMode]);

  return (
    <>
      {/*
       * Backdrop custom :
       * - z-[48] : sous le panel Sheet (z-50) mais au-dessus du contenu de page
       * - pointer-events actifs → clic pour fermer
       * - Le lecteur flottant (z-[70]) serait au-dessus si visible, mais il est
       *   masqué quand la bibliothèque est ouverte (voir GlobalAudioPlayer)
       */}
      {libraryOpen && (
        <div
          className="fixed inset-0 z-[48] bg-black/50 backdrop-blur-[1px] transition-opacity"
          aria-hidden="true"
          onClick={() => setLibraryOpen(false)}
        />
      )}

      {/*
       * modal={false} → CORRECTIF PRINCIPAL
       * Désactive le FocusScope trapped={true} de Radix UI qui interceptait
       * tous les pointer-events hors de la dialog (quelle que soit la valeur
       * de z-index CSS).
       * Désactive aussi le RemoveScroll (scroll lock).
       *
       * La fermeture reste assurée par :
       *   - Touche ESC (géré par Radix)
       *   - Bouton X (SheetPrimitive.Close)
       *   - Clic sur le backdrop custom (ci-dessus)
       */}
      <Sheet open={libraryOpen} onOpenChange={setLibraryOpen} modal={false}>
        <SheetContent
          side={isMobile ? 'bottom' : 'right'}
          hideOverlay
          className={cn(
            'w-full sm:max-w-2xl p-0 gap-0',
            isMobile && 'h-[88vh] rounded-t-xl border-t',
          )}
        >
          <div className="flex h-full flex-col overflow-hidden">
            {/* En-tête du panel */}
            <SheetHeader className="flex-shrink-0 px-4 py-3 border-b border-border/60">
              <SheetTitle className="flex items-center gap-2">
                <BookAudio className="h-5 w-5" />
                Bibliotheque audio
              </SheetTitle>
            </SheetHeader>

            {/* ── Dock lecteur intégré ── toujours cliquable */}
            <PlayerDock />

            {/* Filtres / recherche */}
            <div className="flex-shrink-0 px-4 py-3 space-y-3 border-b border-border/60">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Rechercher un titre..."
                    className="pl-8"
                  />
                </div>
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Reset
                </Button>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs text-muted-foreground">Tags:</Label>
                {tagCounts.map(({ tag, count }) => (
                  <Button
                    key={tag}
                    variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                    size="sm"
                    className="h-7"
                    onClick={() => toggleTag(tag)}
                  >
                    {formatTagLabel(tag)} ({count})
                  </Button>
                ))}
                {uncategorizedTracks.length > 0 && (
                  <Badge variant="outline" className="h-7 flex items-center px-2">
                    Non classe ({uncategorizedTracks.length})
                  </Badge>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-xs text-muted-foreground">Filtre tags:</Label>
                <Button
                  size="sm"
                  variant={tagMode === 'or' ? 'default' : 'outline'}
                  className="h-7"
                  onClick={() => setTagMode('or')}
                >
                  OR
                </Button>
                <Button
                  size="sm"
                  variant={tagMode === 'and' ? 'default' : 'outline'}
                  className="h-7"
                  onClick={() => setTagMode('and')}
                >
                  AND
                </Button>

                <Label className="ml-3 text-xs text-muted-foreground">Tri:</Label>
                <Button
                  size="sm"
                  variant={sortMode === 'az' ? 'default' : 'outline'}
                  className="h-7"
                  onClick={() => setSortMode('az')}
                >
                  A-Z
                </Button>
                <Button
                  size="sm"
                  variant={sortMode === 'recent' ? 'default' : 'outline'}
                  className="h-7"
                  onClick={() => setSortMode('recent')}
                >
                  Recents
                </Button>
                <Button
                  size="sm"
                  variant={sortMode === 'duration' ? 'default' : 'outline'}
                  className="h-7"
                  onClick={() => setSortMode('duration')}
                >
                  Duree
                </Button>
              </div>
            </div>

            <Tabs value={libraryTab} onValueChange={setLibraryTab} className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-shrink-0 px-4 pt-3">
                <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full">
                  <TabsTrigger value="tags">Tags</TabsTrigger>
                  <TabsTrigger value="collections">Listes</TabsTrigger>
                  <TabsTrigger value="all">Tous</TabsTrigger>
                  <TabsTrigger value="favorites">Favoris</TabsTrigger>
                  <TabsTrigger value="recents">Récents</TabsTrigger>
                  <TabsTrigger value="queue">Queue</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="tags" className="mt-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full px-4 pb-4">
                  <div className="space-y-2 pt-3">
                    {filteredTracks.map((track) => (
                      <LibraryTrackRow
                        key={track.id}
                        track={track}
                        isFavorite={favoritesSet.has(track.id)}
                        onPlay={(id) => playTrackById(id, { forceNoFade: false })}
                        onAddNext={addToQueueNext}
                        onAddEnd={addToQueueEnd}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Tab Collections */}
              <TabsContent value="collections" className="mt-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full px-4 pb-4">
                  <div className="space-y-4 pt-3">
                    {collections.length === 0 && (
                      <p className="text-sm text-muted-foreground">Aucune collection disponible.</p>
                    )}
                    {collections.map((col) => {
                      const colTracks = (col.track_ids || [])
                        .map((id) => tracksById.get(id))
                        .filter(Boolean);
                      return (
                        <div key={col.id} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {col.cover_url && (
                                <img src={col.cover_url} alt="" width={32} height={32} className="rounded object-cover" />
                              )}
                              <div>
                                <p className="text-sm font-semibold">{col.name}</p>
                                {col.description && (
                                  <p className="text-xs text-muted-foreground">{col.description}</p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7"
                              onClick={() => playCollectionRandom(col.id)}
                              disabled={!colTracks.length}
                              title="Lecture aléatoire"
                            >
                              <Shuffle className="h-3 w-3 mr-1" />
                              Shuffle
                            </Button>
                          </div>
                          <div className="space-y-1 pl-2">
                            {colTracks.map((track) => (
                              <LibraryTrackRow
                                key={track.id}
                                track={track}
                                isFavorite={favoritesSet.has(track.id)}
                                onPlay={(id) => playTrackById(id, { forceNoFade: false })}
                                onAddNext={addToQueueNext}
                                onAddEnd={addToQueueEnd}
                                onToggleFavorite={toggleFavorite}
                              />
                            ))}
                            {colTracks.length === 0 && (
                              <p className="text-xs text-muted-foreground">Collection vide.</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="all" className="mt-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full px-4 pb-4">
                  <div className="space-y-2 pt-3">
                    {filteredTracks.map((track) => (
                      <LibraryTrackRow
                        key={track.id}
                        track={track}
                        isFavorite={favoritesSet.has(track.id)}
                        onPlay={(id) => playTrackById(id, { forceNoFade: false })}
                        onAddNext={addToQueueNext}
                        onAddEnd={addToQueueEnd}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="favorites" className="mt-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full px-4 pb-4">
                  <div className="space-y-2 pt-3">
                    {filteredTracks.map((track) => (
                      <LibraryTrackRow
                        key={track.id}
                        track={track}
                        isFavorite={favoritesSet.has(track.id)}
                        onPlay={(id) => playTrackById(id, { forceNoFade: false })}
                        onAddNext={addToQueueNext}
                        onAddEnd={addToQueueEnd}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                    {!filteredTracks.length && <p className="text-sm text-muted-foreground">Aucun favori.</p>}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="recents" className="mt-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full px-4 pb-4">
                  <div className="space-y-2 pt-3">
                    {filteredTracks.map((track) => (
                      <LibraryTrackRow
                        key={track.id}
                        track={track}
                        isFavorite={favoritesSet.has(track.id)}
                        onPlay={(id) => playTrackById(id, { forceNoFade: false })}
                        onAddNext={addToQueueNext}
                        onAddEnd={addToQueueEnd}
                        onToggleFavorite={toggleFavorite}
                      />
                    ))}
                    {!filteredTracks.length && <p className="text-sm text-muted-foreground">Aucun recent.</p>}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="queue" className="mt-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full px-4 pb-4">
                  <div className="pt-3">
                    <QueueList
                      tracksById={tracksById}
                      queue={queue}
                      currentTrackId={currentTrackId}
                      onPlay={playFromQueue}
                      onRemove={removeFromQueue}
                    />
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// GlobalAudioPlayer — lecteur flottant + bibliothèque
//
// Quand la bibliothèque est ouverte, le lecteur flottant se masque :
// le PlayerDock intégré dans le panel prend le relais.
// Quand la bibliothèque se ferme, le lecteur flottant revient.
// ─────────────────────────────────────────────────────────────────────────────
export const GlobalAudioPlayer = () => {
  const {
    currentTrack,
    queue,
    isPlaying,
    progress,
    duration,
    volume,
    muted,
    repeatMode,
    crossfadeEnabled,
    crossfadeDuration,
    crossfadeOptions,
    displayMode,
    libraryOpen,
    isMobile,
    setDisplayMode,
    togglePlayPause,
    playNext,
    playPrevious,
    seekTo,
    stopImmediate,
    setVolume,
    toggleMute,
    cycleRepeatMode,
    setCrossfadeEnabled,
    setCrossfadeDuration,
    openLibrary,
    favoritesSet,
    toggleFavorite,
  } = useAudioPlayer();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeModule, setActiveModule] = useState('music'); // 'music' | 'soundboard'
  const [travelWorkspacePlayerOpen, setTravelWorkspacePlayerOpen] = useState(false);
  const location = useLocation();
  const isTravelMapSetup = /^\/campaigns\/[^/]+\/maps\/[^/]+\/travel-setup\/?$/.test(location.pathname);
  const isTravelRun = /^\/campaigns\/[^/]+\/travel\/?$/.test(location.pathname)
    && new URLSearchParams(location.search).get('view') === 'run';
  const isFocusedTravelWorkspace = isTravelMapSetup || isTravelRun;

  useEffect(() => {
    setTravelWorkspacePlayerOpen(false);
  }, [location.pathname, location.search]);

  // Language for localized sound names
  const { language } = useAuth();

  // Soundboard hook — always called (hooks can't be conditional)
  const sb = useSoundboard(language || 'fr');

  const safeAreaPadding = {
    paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.45rem)',
  };

  const repeatIcon = repeatMode === 'one' ? Repeat1 : Repeat;
  const RepeatIcon = repeatIcon;
  const trackTitle = currentTrack?.title || currentTrack?.filename || 'Aucune piste selectionnee';
  const trackSubtitle = currentTrack?.filename || 'Choisissez une ambiance dans la bibliotheque';
  const isFavorite = currentTrack ? favoritesSet.has(currentTrack.id) : false;
  // Map preparation is a full-screen precision workspace. Keep the global
  // player behind its compact launcher until the MJ explicitly opens it.
  const effectiveDisplayMode = isFocusedTravelWorkspace && displayMode === 'normal' && !travelWorkspacePlayerOpen
    ? 'hidden'
    : displayMode;

  // Le lecteur flottant se masque quand une bibliothèque est ouverte.
  const showFloatingPlayer = !libraryOpen && !sb.libraryOpen;

  if (effectiveDisplayMode === 'hidden') {
    return (
      <>
        {showFloatingPlayer && (
          <div
            className={isTravelRun ? 'fixed bottom-[16.5rem] right-4 z-[70]' : 'fixed bottom-2 right-2 z-[70]'}
            style={safeAreaPadding}
          >
            <Button
              variant="secondary"
              size={isTravelRun ? 'sm' : 'icon'}
              className={isTravelRun ? 'gap-2 border border-white/10 bg-black/75 text-white shadow-lg backdrop-blur hover:bg-black/90 hover:text-white' : 'rounded-full shadow-lg'}
              onClick={() => {
                if (isFocusedTravelWorkspace) setTravelWorkspacePlayerOpen(true);
                setDisplayMode(isMobile ? 'minimal' : 'normal');
              }}
              aria-label="Ouvrir le player"
            >
              <Music2 className="h-5 w-5" />
              {isTravelRun && <span>Musique</span>}
            </Button>
          </div>
        )}
        <AudioLibrary />
        <SoundboardLibrary
          open={sb.libraryOpen}
          onOpenChange={sb.setLibraryOpen}
          categories={sb.categories}
          selectedCategoryId={sb.selectedCategoryId}
          onSelectCategory={sb.setSelectedCategoryId}
          filteredSounds={sb.filteredSounds}
          activeSoundIds={sb.activeSoundIds}
          activeSounds={sb.activeSounds}
          soundVolumes={sb.soundVolumes}
          masterVolume={sb.masterVolume}
          defaultSoundVolume={sb.DEFAULT_SOUND_VOLUME}
          searchTerm={sb.searchTerm}
          onSearchChange={sb.setSearchTerm}
          onToggleSound={sb.toggleSound}
          onStopSound={sb.stopSound}
          onStopAll={sb.stopAll}
          onSetSoundVolume={sb.setSoundVolume}
          onSetMasterVolume={sb.setMasterVolume}
          isMobile={isMobile}
          lang={language}
        />
      </>
    );
  }

  const containerClass = isMobile
    ? 'fixed inset-x-0 bottom-0 z-[70] px-2'
    : 'fixed bottom-4 right-4 z-[70] w-[min(420px,calc(100vw-2rem))]';

  if (effectiveDisplayMode === 'minimal') {
    return (
      <>
        {showFloatingPlayer && (
          <div className={containerClass} style={safeAreaPadding}>
            <div className="glass-card rounded-xl border border-border/80 px-2 py-2 shadow-2xl">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={togglePlayPause} aria-label="Lecture ou pause">
                  {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                </Button>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{trackTitle}</p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    if (activeModule === 'soundboard') {
                      sb.setLibraryOpen(true);
                    } else {
                      openLibrary(DEFAULT_LIBRARY_TAB);
                    }
                  }}
                  aria-label="Ouvrir bibliotheque"
                >
                  {activeModule === 'soundboard' ? <AudioLines className="h-4 w-4" /> : <BookAudio className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setActiveModule(activeModule === 'music' ? 'soundboard' : 'music')}
                  aria-label="Basculer module"
                  className={cn(activeModule === 'soundboard' && 'text-primary')}
                >
                  {activeModule === 'soundboard' ? <Music2 className="h-4 w-4" /> : <AudioLines className="h-4 w-4" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDisplayMode('normal')} aria-label="Mode normal">
                  <PanelBottom className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDisplayMode('hidden')} aria-label="Cacher player">
                  <EyeOff className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}

        <AudioLibrary />
        <SoundboardLibrary
          open={sb.libraryOpen}
          onOpenChange={sb.setLibraryOpen}
          categories={sb.categories}
          selectedCategoryId={sb.selectedCategoryId}
          onSelectCategory={sb.setSelectedCategoryId}
          filteredSounds={sb.filteredSounds}
          activeSoundIds={sb.activeSoundIds}
          activeSounds={sb.activeSounds}
          soundVolumes={sb.soundVolumes}
          masterVolume={sb.masterVolume}
          defaultSoundVolume={sb.DEFAULT_SOUND_VOLUME}
          searchTerm={sb.searchTerm}
          onSearchChange={sb.setSearchTerm}
          onToggleSound={sb.toggleSound}
          onStopSound={sb.stopSound}
          onStopAll={sb.stopAll}
          onSetSoundVolume={sb.setSoundVolume}
          onSetMasterVolume={sb.setMasterVolume}
          isMobile={isMobile}
          lang={language}
        />
      </>
    );
  }

  // displayMode === 'normal'
  return (
    <>
      {showFloatingPlayer && (
        <div className={containerClass} style={safeAreaPadding}>
          <div className="glass-card rounded-xl border border-border/80 p-3 shadow-2xl">
            {/* ── Module toggle: Musique / Soundboard ── */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-0.5 rounded-lg border border-border/60 bg-muted/30 p-0.5">
                <button
                  type="button"
                  onClick={() => setActiveModule('music')}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
                    activeModule === 'music'
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Music2 className="h-3.5 w-3.5" />
                  Musique
                </button>
                <button
                  type="button"
                  onClick={() => setActiveModule('soundboard')}
                  className={cn(
                    'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
                    activeModule === 'soundboard'
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <AudioLines className="h-3.5 w-3.5" />
                  Soundboard
                  {sb.activeSounds.length > 0 && (
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 h-3.5 ml-0.5">
                      {sb.activeSounds.length}
                    </Badge>
                  )}
                </button>
              </div>

              <div className="flex items-center gap-0.5">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDisplayMode('minimal')} aria-label="Mode minimal">
                  <PanelBottom className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDisplayMode('hidden')} aria-label="Cacher">
                  <EyeOff className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* ── Music module content ── */}
            {activeModule === 'music' && (
              <>
                <div className="flex items-start gap-3">
                  <ArtworkThumb src={currentTrack?.imageUrl} alt={trackTitle} size={64} />

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{trackTitle}</p>
                        <p className="truncate text-xs text-muted-foreground">{trackSubtitle}</p>
                      </div>
                      {currentTrack && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(isFavorite && 'text-amber-500')}
                          onClick={() => toggleFavorite(currentTrack.id)}
                          aria-label="Favori"
                        >
                          <Star className={cn('h-4 w-4', isFavorite && 'fill-amber-500')} />
                        </Button>
                      )}
                    </div>

                    <div className="mt-3 flex items-center gap-1">
                      <Button variant="ghost" size="icon" onClick={playPrevious} aria-label="Precedent">
                        <SkipBack className="h-5 w-5" />
                      </Button>
                      <Button variant="default" size="icon" onClick={togglePlayPause} aria-label="Lecture ou pause">
                        {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                      </Button>
                      <Button variant="ghost" size="icon" onClick={playNext} aria-label="Suivant">
                        <SkipForward className="h-5 w-5" />
                      </Button>

                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(repeatMode !== 'off' && 'text-primary')}
                          onClick={cycleRepeatMode}
                          aria-label="Boucle"
                        >
                          <RepeatIcon className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openLibrary('queue')} aria-label="Queue">
                          <ListMusic className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openLibrary('tags')} aria-label="Bibliotheque">
                          <BookAudio className="h-4 w-4" />
                        </Button>
                        <Button variant="destructive" size="icon" onClick={stopImmediate} aria-label="Stop immediat">
                          <Square className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>

                    <div className="mt-3 space-y-1">
                      <Slider
                        value={[duration > 0 ? (progress / duration) * 100 : 0]}
                        onValueCommit={([ratio]) => seekTo((ratio / 100) * duration)}
                        max={100}
                        step={0.1}
                        disabled={!currentTrack || duration <= 0}
                      />
                      <div className="flex justify-between text-[11px] text-muted-foreground">
                        <span>{formatDuration(progress)}</span>
                        <span>{formatDuration(duration)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator className="my-3" />

                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" onClick={toggleMute} aria-label="Mute">
                    {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </Button>
                  <Slider
                    value={[muted ? 0 : volume * 100]}
                    onValueChange={([value]) => setVolume(value / 100)}
                    max={100}
                    step={1}
                  />
                  <span className="w-10 text-right text-xs text-muted-foreground">{Math.round((muted ? 0 : volume) * 100)}%</span>
                </div>

                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="text-xs text-muted-foreground">Queue: {queue.length} pistes</div>
                  <Button variant="outline" size="sm" onClick={() => setSettingsOpen((previous) => !previous)}>
                    <Settings2 className="h-4 w-4" />
                    Reglages
                  </Button>
                </div>

                {settingsOpen && (
                  <div className="mt-3 rounded-md border border-border/60 bg-muted/30 p-2 space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Crossfade</Label>
                      <Switch checked={crossfadeEnabled} onCheckedChange={setCrossfadeEnabled} />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Duree fondu</Label>
                      <div className="flex flex-wrap gap-1">
                        {crossfadeOptions.map((option) => (
                          <Button
                            key={option}
                            size="sm"
                            variant={crossfadeDuration === option ? 'default' : 'outline'}
                            onClick={() => setCrossfadeDuration(option)}
                          >
                            {option}s
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Raccourcis desktop: Space (Play/Pause), N (Next), M (Mute), +/- (Volume)
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── Soundboard module content ── */}
            {activeModule === 'soundboard' && (
              <SoundboardPanel
                activeSounds={sb.activeSounds}
                soundVolumes={sb.soundVolumes}
                masterVolume={sb.masterVolume}
                defaultSoundVolume={sb.DEFAULT_SOUND_VOLUME}
                onStopSound={sb.stopSound}
                onStopAll={sb.stopAll}
                onSetSoundVolume={sb.setSoundVolume}
                onSetMasterVolume={sb.setMasterVolume}
                onOpenLibrary={() => sb.setLibraryOpen(true)}
                lang={language}
              />
            )}
          </div>
        </div>
      )}

      <AudioLibrary />
      <SoundboardLibrary
        open={sb.libraryOpen}
        onOpenChange={sb.setLibraryOpen}
        categories={sb.categories}
        selectedCategoryId={sb.selectedCategoryId}
        onSelectCategory={sb.setSelectedCategoryId}
        filteredSounds={sb.filteredSounds}
        activeSoundIds={sb.activeSoundIds}
        activeSounds={sb.activeSounds}
        soundVolumes={sb.soundVolumes}
        masterVolume={sb.masterVolume}
        defaultSoundVolume={sb.DEFAULT_SOUND_VOLUME}
        searchTerm={sb.searchTerm}
        onSearchChange={sb.setSearchTerm}
        onToggleSound={sb.toggleSound}
        onStopSound={sb.stopSound}
        onStopAll={sb.stopAll}
        onSetSoundVolume={sb.setSoundVolume}
        onSetMasterVolume={sb.setMasterVolume}
        isMobile={isMobile}
      />
    </>
  );
};

export default GlobalAudioPlayer;
